import { createClient, deleteClient } from '../api/clients'
import { createDeal, deleteDeal } from '../api/deals'
import { createContract, deleteContract } from '../api/contracts'
import { createTask, deleteTask } from '../api/tasks'
import { createComplianceItem, deleteComplianceItem } from '../api/compliance'
import { pb, escapeFilterValue } from '../lib/pocketbase'

const DEMO_TAG = '[DEMO]'
const DEMO_KEY_PREFIX = 'reppr_demo_data_loaded_'

function demoKey(agencyId: string) {
  return `${DEMO_KEY_PREFIX}${agencyId}`
}

function addDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().split('T')[0]
}

export async function seedDemoData(agencyId: string, userId: string): Promise<void> {
  // Create Marcus Thompson — NFL WR, active roster
  const marcus = await createClient(
    {
      first_name: 'Marcus',
      last_name: 'Thompson',
      sport: 'Football',
      league: 'NFL',
      team_name: 'Dallas Cowboys',
      position: 'WR',
      status: 'active',
      priority: 'high',
      notes: `${DEMO_TAG} Sample client for onboarding preview`,
    },
    userId
  )

  // Create DeShawn Carter — NBA SF, free agent
  const deshawn = await createClient(
    {
      first_name: 'DeShawn',
      last_name: 'Carter',
      sport: 'Basketball',
      league: 'NBA',
      position: 'SF',
      status: 'free_agent',
      priority: 'medium',
      notes: `${DEMO_TAG} Sample client for onboarding preview`,
    },
    userId
  )

  // Create Marcus's Nike deal — Negotiating
  await createDeal(
    {
      title: 'Nike Endorsement Deal',
      deal_type: 'endorsement',
      stage: 'Negotiating',
      client_id: marcus.id,
      value: 2400000,
      commission_rate: 10,
      expected_close: addDays(45),
      notes: `${DEMO_TAG} Sample deal for onboarding preview`,
    },
    userId
  )

  // Create DeShawn's New Balance deal — Prospect
  await createDeal(
    {
      title: 'New Balance Partnership',
      deal_type: 'endorsement',
      stage: 'Prospect',
      client_id: deshawn.id,
      value: 800000,
      commission_rate: 10,
      expected_close: addDays(90),
      notes: `${DEMO_TAG} Sample deal for onboarding preview`,
    },
    userId
  )

  // Create Marcus's playing contract with incentives
  await createContract(
    {
      title: 'Marcus Thompson — Cowboys Contract',
      contract_type: 'playing',
      status: 'active',
      client_id: marcus.id,
      total_value: 18000000,
      guaranteed_value: 10000000,
      apy: 4500000,
      contract_years: 4,
      start_date: '2023-09-01',
      end_date: '2027-03-01',
      team_or_brand: 'Dallas Cowboys',
      league: 'NFL',
      notes: `${DEMO_TAG} Sample contract for onboarding preview`,
      incentives: [
        {
          id: 'demo-inc-1',
          category: 'performance',
          description: '1,000+ receiving yards',
          threshold: 1000,
          metric: 'manual',
          value: 250000,
          status: 'in_progress',
          current_progress: 0,
          tracking_type: 'manual',
        },
        {
          id: 'demo-inc-2',
          category: 'individual_award',
          description: 'Pro Bowl selection',
          threshold: 1,
          metric: 'manual',
          value: 500000,
          status: 'not_started',
          current_progress: 0,
          tracking_type: 'manual',
        },
        {
          id: 'demo-inc-3',
          category: 'playing_time',
          description: 'Play 80% of offensive snaps',
          threshold: 80,
          metric: 'manual',
          value: 150000,
          status: 'in_progress',
          current_progress: 0,
          tracking_type: 'manual',
        },
      ],
    },
    userId
  )

  // Create two tasks
  await createTask({
    title: 'Follow up with Marcus on contract extension',
    status: 'todo',
    priority: 'high',
    due_date: addDays(2),
    client_id: marcus.id,
  })

  await createTask({
    title: 'DeShawn free agency review call',
    status: 'todo',
    priority: 'medium',
    due_date: addDays(5),
    client_id: deshawn.id,
  })

  // Create compliance item
  await createComplianceItem(
    {
      title: 'Marcus contract option deadline',
      type: 'filing',
      status: 'pending',
      due_date: addDays(30),
      client_id: marcus.id,
      notes: `${DEMO_TAG} Sample compliance item for onboarding preview`,
      reminder_days_before: 7,
    },
    userId
  )

  localStorage.setItem(demoKey(agencyId), 'true')
}

export async function clearDemoData(agencyId: string): Promise<void> {
  const escAgency = escapeFilterValue(agencyId)
  const escTag = escapeFilterValue(DEMO_TAG)

  const [clients, deals, contracts, tasks, compliance] = await Promise.all([
    pb.collection('clients').getFullList({ filter: `agency_id = "${escAgency}" && notes ~ "${escTag}"` }),
    pb.collection('deals').getFullList({ filter: `agency_id = "${escAgency}" && notes ~ "${escTag}"` }),
    pb.collection('contracts').getFullList({ filter: `agency_id = "${escAgency}" && notes ~ "${escTag}"` }),
    pb.collection('tasks').getFullList({ filter: `agency_id = "${escAgency}" && notes ~ "${escTag}"` }),
    pb.collection('compliance_items').getFullList({ filter: `agency_id = "${escAgency}" && notes ~ "${escTag}"` }),
  ])

  await Promise.all([
    ...tasks.map(r => deleteTask(r.id)),
    ...compliance.map(r => deleteComplianceItem(r.id)),
    ...contracts.map(r => deleteContract(r.id)),
    ...deals.map(r => deleteDeal(r.id)),
  ])

  // Clients last (cascade deletes their sub-records)
  for (const r of clients) {
    await deleteClient(r.id)
  }

  localStorage.removeItem(demoKey(agencyId))
}

export async function hasDemoData(agencyId: string): Promise<boolean> {
  // Fast path: localStorage flag set by this browser
  if (localStorage.getItem(demoKey(agencyId)) === 'true') return true
  // Slow path: check PocketBase so fresh browsers and cross-device users see demo data
  try {
    const esc = escapeFilterValue(agencyId)
    const result = await pb.collection('clients').getList(1, 1, {
      filter: `agency_id = "${esc}" && notes ~ "${escapeFilterValue(DEMO_TAG)}"`,
    })
    if (result.totalItems > 0) {
      localStorage.setItem(demoKey(agencyId), 'true')
      return true
    }
  } catch {
    // If PocketBase is unreachable, fall through to false
  }
  return false
}
